import { useState } from 'react';
import { formatMinutes, type ActivityRange } from '@/lib/activityQueries';
import { cn } from '@/lib/utils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LEARNING TIME, AS A LINE
 *
 * It was a row of bars. Bars are for comparing discrete things to each other;
 * this is one quantity moving through time, which is what a line is for — and
 * on a month where somebody was active twice, thirty-one bars of which
 * twenty-nine are stubs read as a broken chart rather than a quiet month.
 *
 * ── Drawn in a fixed viewBox, stretched by CSS ─────────────────────────────
 * `preserveAspectRatio="none"` lets one set of coordinates fill any width, so
 * the geometry below never has to know how wide the card is. The cost is that
 * strokes would stretch with it — which is why every stroke here is given
 * `vector-effect="non-scaling-stroke"`, so a 2px line stays 2px at any width
 * instead of becoming a wedge.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** The drawing box. Y runs 0–100 with the baseline at 100. */
export const VIEW = { width: 300, height: 100 };

export interface Point {
  x: number;
  y: number;
  bucket: ActivityRange['buckets'][number];
}

export function toPoints(buckets: ActivityRange['buckets'], peak: number): Point[] {
  /**
   * A single bucket sits in the middle rather than at x=0.
   *
   * Dividing by `length - 1` is the natural spacing and it divides by zero on
   * one point — which renders as `NaN` in the path and draws nothing at all.
   */
  const step = buckets.length > 1 ? VIEW.width / (buckets.length - 1) : 0;

  return buckets.map((bucket, i) => ({
    x: buckets.length > 1 ? i * step : VIEW.width / 2,
    /* 4px of headroom, so the tallest point is a peak rather than a line
       clipped by the top of the box. */
    y: VIEW.height - (bucket.minutes / peak) * (VIEW.height - 4),
    bucket,
  }));
}

export function ActivityLineChart({
  buckets,
  peak,
  dimmed = false,
}: {
  buckets: ActivityRange['buckets'];
  /** The scale's top, in minutes. Never zero — an empty chart still draws. */
  peak: number;
  dimmed?: boolean;
}) {
  const [hover, setHover] = useState<number | null>(null);

  if (buckets.length === 0) return null;

  const points = toPoints(buckets, peak);
  const line = points.map((p) => `${p.x},${p.y}`).join(' ');
  /* The same line closed down to the baseline, for the fill underneath. */
  const area = `${points[0]!.x},${VIEW.height} ${line} ${points.at(-1)!.x},${VIEW.height}`;

  const active = hover === null ? null : points[hover];

  return (
    <div className={cn('mt-5 transition-opacity', dimmed && 'opacity-50')}>
      <div className="relative h-[104px]">
        <svg
          viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
          preserveAspectRatio="none"
          /* `text-brand-800` so the gradient below can use `currentColor` —
             the brand scale is a literal palette, not a CSS variable, so
             `hsl(var(--brand-800))` would resolve to nothing. */
          className="h-full w-full overflow-visible text-brand-800"
          role="img"
          aria-label="Learning time per day"
        >
          <defs>
            <linearGradient id="activity-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Recessive baseline. The only rule on the plot. */}
          <line
            x1="0" y1={VIEW.height} x2={VIEW.width} y2={VIEW.height}
            className="stroke-border"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />

          <polygon points={area} fill="url(#activity-fill)" />

          <polyline
            points={line}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />

          {/*
            A marker only where something happened.
            
            A dot on every one of thirty-one days is a dotted line; a dot on
            the two days somebody actually learned is the information.
          */}
          {points.map((p, i) =>
            p.bucket.minutes > 0 ? (
              <circle
                key={p.bucket.key}
                cx={p.x}
                cy={p.y}
                r={hover === i ? 5 : 3.5}
                fill="currentColor"
                className="stroke-card"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            ) : null,
          )}

          {active && (
            <line
              x1={active.x} y1="0" x2={active.x} y2={VIEW.height}
              className="stroke-border"
              strokeWidth="1"
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>

        {/*
          The hover layer, as one invisible column per bucket.

          Hit targets have to be much bigger than the marks — a 3.5px dot on a
          day nobody can point at is not hoverable — so the whole column is the
          target and the tooltip follows whichever column the cursor is over.
        */}
        <div className="absolute inset-0 flex">
          {buckets.map((bucket, i) => (
            <button
              key={bucket.key}
              type="button"
              tabIndex={-1}
              aria-label={`${bucket.tooltip}: ${formatMinutes(bucket.minutes)}`}
              className="h-full flex-1 cursor-default"
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onBlur={() => setHover(null)}
            />
          ))}
        </div>

        {active && (
          <div
            className="pointer-events-none absolute -top-1 z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background shadow-md"
            style={{ left: `${(active.x / VIEW.width) * 100}%` }}
          >
            {formatMinutes(active.bucket.minutes)}
            <span className="ml-1.5 opacity-70">{active.bucket.tooltip}</span>
          </div>
        )}
      </div>

      {/* The axis, on the same flex rhythm as the hover columns above it so a
          tick sits under the point it labels. */}
      <div className="mt-1.5 flex">
        {buckets.map((bucket) => (
          <span
            key={bucket.key}
            className="flex-1 text-center text-[10px] leading-[13px] text-muted-foreground"
          >
            {bucket.label}
          </span>
        ))}
      </div>
    </div>
  );
}
